import React from 'react'
import { MoveFadeInAnimation } from '@/lib/hooks/use-animations'
import { Container, Link, Typography } from '@mui/material';
import { Image, HeadingText, ButtonOne, ButtonNew } from '@/components/shared';
import NextImage from 'next/image';
import { capitalize, convertMarkdownLinks } from '@/lib/utils';
import { useTranslations } from 'next-intl';
import { ComponentProps, DefaultPageProps } from '@/lib/types/shared/page.types';
import { ButtonOneProps } from '@/components/shared/ButtonOne';
import heroImages, { HeroImage, HeroImageItem } from '@/lib/constants/hero-images';
import { Cursor } from '@/components/shared/icons';
import { Breadcrumb } from '@/components/shared';
import cx from "classnames";

type MaskedImageProps = {
    showBackground?: boolean,
    wrapperStyle?: React.CSSProperties,
    className?: string,
    style?: React.CSSProperties,
    image: HeroImage,
}

type Props = {
    className?: string,
    
    title: string,
    subTitle?: string,
    text?: string,
    convertTitleToLink?: boolean,
    convertSubTitleToLink?: boolean,
    convertTextToLink?: boolean,
    
    subTitleClassName?: string,
    titleClassName?: string,
    
    children?: React.ReactNode,
    customChildren?: React.ReactNode,
    buttons?: ButtonOneProps[],

    maskedImages?: MaskedImageProps[],
} & DefaultPageProps & ComponentProps;

const MaskedImage = function ({
    showBackground = true,
    wrapperStyle = {
        bottom: '-30%',
        left: '55%',
        transform: 'translateX(-50%)',
        width: '35%',
    },
    className = '',
    // style = {
    //     backgroundPosition: 'bottom center',
    //     backgroundSize: '100%',
    // },
    image,
}: MaskedImageProps) {

    const imageData = heroImages[image];

    const ForeGroundImage = ({ imageData }: { imageData: HeroImageItem }) => {

        return (
            <div className="relative w-full h-full">
                { imageData.type === 'video' ? (
                    <video autoPlay muted loop className="w-full h-full object-center object-cover">
                        {imageData.source.map((source, index) => (
                            <source key={index} src={source.src} type={source.type} />
                        ))}
                    </video>
                ) : (
                    <NextImage src={imageData.source[0].src} alt="" fill className="w-full h-full object-center object-cover" />
                )}
            </div>
        );
    }

    return (
        <>
            <div className={cx('masked-image-wrapper z-10', className)} style={wrapperStyle}>
                {showBackground ? (
                    <>
                        <div className="background-svg-wrapper">
                            <NextImage className="background-svg" src="/images/hero/masks/outer.svg" alt="" width={300} height={300} />
                        </div>
                        <div className="foreground-svg-wrapper bottom-center">
                            <div className="foreground-svg bg-primary-yellow-400">
                                <ForeGroundImage imageData={imageData} />
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="foreground-svg-only-wrapper">
                        <div className="foreground-svg-only bg-primary-yellow-400">
                            <ForeGroundImage imageData={imageData} />
                        </div>
                    </div>
                )}
            </div>
        </>
    )
}

export default function HeroNd({
    className = 'pb-12',

    title,
    subTitle = '',
    text = 'ISO 27001 & ISO 9001 Gecertificeerd  |  The Developers Award',
    convertTitleToLink,
    convertSubTitleToLink = true,
    convertTextToLink = true,
    
    titleClassName,
    subTitleClassName,

    children = null,
    customChildren = null,
    buttons = [],

    maskedImages = [
        {
            className: 'hidden md:block',
            wrapperStyle: {
                bottom: 'calc(-20vh - -6vw)',
                right: '11vw',
                transform: 'translate(-11vw, 20vh)',
                width: 'calc(35vh + 21.3333vw)',
            },
            image: 'still-webp',
            style: {
                backgroundPosition: 'bottom center',
                backgroundSize: '100%',
                filter: 'brightness(0.8)',
            },
        },
        {
            className: 'block md:hidden',
            wrapperStyle: {
                bottom: '-10%',
                right: '-9vw',
                transform: 'translate(9vw, 20%)',
                width: 'calc(35vh + (32vw / 1.5))',
            },
            image: 'still-webp',
            style: {
                backgroundPosition: 'bottom center',
                backgroundSize: '100%',
                filter: 'brightness(0.8)',
            },
        },
        {
            showBackground: false,
            className: 'hidden md:block',
            wrapperStyle: {
                bottom: 'calc(0vh - -6vw)',
                right: '-5vw',
                transform: 'translate(5vw, 0)',
                width: 'calc(35vh + 21.3333vw)',
            },
            image: 'hero-5',
            style: {
                backgroundPosition: 'center',
                backgroundSize: '100%',
            },
        },
    ],
    params
}: Props) {
    const t = useTranslations();

    const customIcon = (variant: string) => {
        return (
            <div className={cx('rounded-lg p-2', variant === 'green' ? 'bg-primary-yellow-t' : 'bg-primary-yellow-t')}>
                <Cursor color='fill-primary-yellow-400' />
            </div>
        )
    }

    // Only allowed text:
    text = 'ISO 27001 & ISO 9001 Gecertificeerd  |  The Developers Award'

    return (
        <div className={cx('bg-white w-full', className)}>
            <div className={cx('pt-24 pb-8 md:pb-28 px-5 bg-primary-yellow-400 w-full relative overflow-hidden')}>
                <div className="w-full h-full bg-gradient-to-r from-black/60 via-50% via-black/40 to-60% to-black/20 absolute top-0 left-0 z-0"></div>
                <Container className='relative z-20'>

                    <div className="mt-4">
                        <Breadcrumb params={params} />
                    </div>

                    {title !== undefined && title !== null &&
                        <MoveFadeInAnimation direction="right" distance="xl" amount={0.5}>
                            <HeadingText variant='h1' enableMaxWidth={false} className={cx(`md:text-5xl lg:text-6xl md:leading-extra-tight max-w-2xl my-3 ${titleClassName}`)}>
                                {convertTitleToLink ?
                                    <span className={cx('md:text-5xl lg:text-6xl md:leading-extra-tight', titleClassName)} dangerouslySetInnerHTML={{
                                        __html: capitalize(convertMarkdownLinks(title, params.locale))
                                    }}></span>
                                    : capitalize(title)}
                            </HeadingText>
                        </MoveFadeInAnimation>
                    }

                    {subTitle !== undefined && subTitle !== null &&
                        <MoveFadeInAnimation delay={200} direction="right" distance="xl" amount={0.5}>
                            {convertSubTitleToLink ? (
                                <Typography variant='h6' component='h2' className={cx(`mb-0 overide-static-font max-w-2xl`, subTitleClassName)} dangerouslySetInnerHTML={{
                                    __html: convertMarkdownLinks(subTitle, params.locale, false, 'no-underline')
                                }} />
                            ) : (
                                <Typography variant='h6' component='h2' className={cx(`mb-0`, subTitleClassName)}>{subTitle}</Typography>
                            )}
                        </MoveFadeInAnimation>
                    }

                    {(text !== undefined && text !== null && text !== '') && (
                        <Typography variant='body1' className={`mt-0 max-w-lg`}>
                            {convertTextToLink ?
                                <span className='text-white text-start normal-case max-w-screen-md' dangerouslySetInnerHTML={{
                                    __html: convertMarkdownLinks(text, params.locale)
                                }}></span>
                                : text}
                        </Typography>
                    )}

                    {buttons && buttons.length > 0 && (
                        <div className="flex flex-wrap items-center gap-4 mt-8">
                            {buttons.map((button, index) => (
                                <MoveFadeInAnimation key={index} direction='right' delay={100 + (index * 100)}>
                                    <ButtonOne paddingX='extra-small' customIcon={typeof button.customIcon === 'undefined' ? customIcon(button.variant ?? 'green') : button.customIcon} iconPos='left' variant={button.variant ? button.variant : 'green'} type={button.type ?? ''} disabled={button.disabled ?? false} text={button.text} title={button.text} ariaLabel={button.text} link={button.link ?? undefined} onclick={button.onclick ?? undefined} className='w-fit ml-0 skip' />
                                </MoveFadeInAnimation>
                            ))}
                        </div>
                    )}


                    {children && (
                        <div className='mt-8'>
                            {children}
                        </div>
                    )}

                    {customChildren && (
                        <div className='mt-8'>
                            {customChildren}
                        </div>
                    )}

                    <div className='flex items-center gap-x-2 mt-4'>
                        <Image
                            src="Icons/Rating-star_yao6fg"
                            alt="Google Reviews"
                            width={200}
                            height={97.45}
                            className="w-16 md:w-28 h-auto"
                        />
                        <Link target='_blank' href={'https://www.google.com/search?sa=X&sca_esv=7d935f78db81bbbe&rlz=1C5CHFA_enNL1023NL1026&tbm=lcl&sxsrf=AHTn8zqhvHN-kPVh-Bs0PsNXHI5OAG7t5w:1739277676950&q=Web+Beukers+Reviews&rflfq=1&num=20&stick=H4sIAAAAAAAAAONgkxI2MTQ0NzEwMLUwt7AwMDc1MTYz38DI-IpRODw1ScEptTQ7tahYISi1LDO1vHgRKzZRAF8f_L1GAAAA&rldimm=4117400587880754367&hl=nl-NL&ved=2ahUKEwjIt-ag0ruLAxV1hP0HHaaWDYUQ9fQKegQITBAF&biw=1411&bih=958&dpr=1#lkt=LocalPoiReviews'} className='flex items-center gap-2 w-fit h-fit text-white'>(4.9/5) {t('read_reviews')}</Link>
                    </div>
                </Container>

                {maskedImages && maskedImages.length > 0 && (
                    <>
                        <svg width="0" height="0" xmlns="http://www.w3.org/2000/svg">
                            <defs>
                                <clipPath id="clipping" clipPathUnits="objectBoundingBox">
                                    <path d="M0.2342275 0.099765125C0.2770825 0.01306375 0.38145 -0.023262125 0.46734 0.018629L0.90296625 0.23109875C0.98885625 0.27299 1.0237425 0.377235 0.9808875 0.46393625L0.76509625 0.90051375C0.72224125 0.987215 0.6178725 1.02354 0.5319825 0.98164875L0.096356125 0.76918C0.0104665625 0.72728875 -0.024419875 0.62304375 0.018435125 0.5363425L0.2342275 0.099765125 Z" />
                                </clipPath>
                            </defs>
                        </svg>
                        {maskedImages.map((maskedImage, index) => (
                            <MaskedImage key={index} {...maskedImage} />
                        ))}
                    </>
                )}
                
            </div>
        </div>
    )
}
